import { AFFIXES, COMPONENTS } from "./affixes";
import type { WeaponId } from "./schemas";
import { pickupFlashColor as affixPickupFlashColor } from "./affix-identity";
import { WEAPONS } from "./weapons";

/** Shared pickup kind tags for world tint, ship flash, and HUD copy. */
export type PickupVisualKind = "salvage" | "weapon" | "component" | "affix" | "elite_core";

export type PickupIdentity = {
  /** Compact uppercase tag, e.g. ORDNANCE. */
  tag: string;
  /** World / flash emissive hex. */
  color: number;
  /** CSS color for HUD / overlays. */
  css: string;
};

export const PICKUP_IDENTITIES: Record<PickupVisualKind, PickupIdentity> = {
  salvage: { tag: "SALVAGE", color: 0x5a8a3a, css: "#5a8a3a" },
  weapon: { tag: "ORDNANCE", color: 0xc45c26, css: "#c45c26" },
  component: { tag: "COMPONENT", color: 0x6ec6ff, css: "#6ec6ff" },
  affix: { tag: "AFFIX", color: 0xd0d4d8, css: "#d0d4d8" },
  elite_core: { tag: "ELITE CORE", color: 0xe8c040, css: "#e8c040" },
};

/** World-drop emissive for a loot kind (affix uses payload tint when known). */
export function pickupWorldColor(kind: PickupVisualKind, payloadId?: string): number {
  if (kind === "affix") return affixPickupFlashColor(payloadId);
  return PICKUP_IDENTITIES[kind].color;
}

/** Ship flash tint after collecting a drop. */
export function pickupCollectFlashColor(kind: PickupVisualKind | null | undefined, payloadId?: string | null): number {
  if (!kind) return 0xd0d4d8;
  if (kind === "affix") return affixPickupFlashColor(payloadId ?? undefined);
  return PICKUP_IDENTITIES[kind].color;
}

/** Display name for a payload (weapon / affix / component). */
export function pickupPayloadName(kind: PickupVisualKind, payloadId: string | undefined): string {
  if (!payloadId) return PICKUP_IDENTITIES[kind].tag;
  if (kind === "weapon") return WEAPONS[payloadId as WeaponId]?.name ?? payloadId;
  if (kind === "affix") return AFFIXES.find((a) => a.id === payloadId)?.name ?? payloadId;
  if (kind === "component") return COMPONENTS.find((c) => c.id === payloadId)?.name ?? payloadId;
  return payloadId;
}

/** Shared pickup announcement language. */
export function pickupAnnounce(
  kind: PickupVisualKind,
  payloadId?: string,
  opts?: { incompatible?: boolean },
): string {
  const tag = PICKUP_IDENTITIES[kind].tag;
  if (opts?.incompatible) {
    if (kind === "affix") return `${tag} incompatible — converted to salvage.`;
    if (kind === "component") return `${tag} bay full — converted to salvage.`;
  }
  if (kind === "salvage") return `${tag} secured.`;
  if (kind === "elite_core") {
    return `${tag} secured — fly to the CARRIER (lit pad + extract ring) to bank loot.`;
  }
  const name = pickupPayloadName(kind, payloadId);
  if (kind === "weapon") return `${tag} acquired: ${name}`;
  if (kind === "component") return `${tag} fitted: ${name}`;
  return `${tag} installed: ${name}`;
}
